import { Injectable, OnDestroy } from '@angular/core';
import { Observable, of, map, BehaviorSubject, Subject, tap } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { AuthService } from './auth.service';

export interface RecurringInfo {
  frequency: number; // 1 = Daily, 2 = Weekly, 3 = Monthly
  daysOfWeek?: number[]; // 0-6 (Sunday-Saturday) for weekly
  monthlyDays?: number[]; // 1-31 for monthly
  endDate?: string; // ISO string
  numberOfOccurrences?: number;
  lastGeneratedDate?: string; // ISO string
}

export interface RecurringAppointmentSchedule {
  id: string;
  customerId: string;
  addressId: string;
  recurringInfo: RecurringInfo;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  updatedBy?: string;
  name?: string;
  phoneNumber?: string;
  address?: string;
  locationName?: string;
}

export interface Appointment {
  id: string;
  customerId: string;
  customerName: string | null;
  phoneNumber: string | null;
  date: string;
  address: string;
  addressId?: string;
  notificationType?: number | null;
  location: location;
  locationName: string;
  geoZoneId: string;
  geoZoneName: string;
  status: string;
  createdAt?: string;
  updatedAt?: string;
  version?: string | null;
  createdBy?: string | null;
  updatedBy?: string | null;
  scrapCollectionId?: string;
  remarks?: string | null;
  remarkHistory?: AppointmentRemark[];
  cancellationReason?: string | null;
  timeSlot?: string;
  pinCode: string;
  agentId: string;
  agentName: string;
  appointmentId: string;
  parentId?: string;
  recurringAppointmentScheduleId?: string | null;
  isCallbackRequired?: boolean;
  isAgreed?: boolean;
  dailyOrder?: number | null;

  collectionCharge?: number | null;
  platformChargeInPercent?: number | null;
  platformChargeSource?: string | null;
  collectionChargeSource?: string | null; // "Global" or "Customer" or "Appointment"

  scrapItems?: AppointmentCatalogSelectionDto[] | null;
  disposableItems?: AppointmentCatalogSelectionDto[] | null;
  scrapPreviewImages?: AppointmentScrapPreviewImageDto[] | null;
  requirements?: AppointmentRequirements | null;
}

export interface AppointmentRequirements {
  tools?: string[];
  vehicleType?: string | null;
  additionalLabourCount?: number;
  heavyLiftingRequired?: boolean;
  specialNotes?: string | null;
}

export interface AppointmentRemark {
  text: string;
  createdBy: string;
  createdAt: string;
}

export interface AppointmentCatalogSelectionDto {
  categoryName: string | null;
}

export interface AppointmentScrapPreviewImageDto {
  blobName: string | null;
  previewDownloadUrl?: string | null;
}

export interface FeedbackQuestionDefinitionDto {
  id: string;
  questionText: string;
  questionType: string;
  isActive: boolean;
}

export interface FeedbackQuestionAnswerDto {
  questionId: string;
  questionText: string;
  answer: string;
}

export interface ScrapCollectionFeedbackDetailsDto {
  feedbackReason: string;
  isManuallyTagged: boolean;
  feedbackStatus: string;
  happinessIndex?: number | null;
  customerComments?: string | null;
  internalComments?: string | null;
  questionAnswers?: FeedbackQuestionAnswerDto[];
  loggedAt?: string | null;
  loggedBy?: string | null;
}

export interface SaveScrapCollectionFeedbackDto {
  happinessIndex?: number | null;
  customerComments?: string | null;
  internalComments?: string | null;
  feedbackStatus?: string;
  questionAnswers?: FeedbackQuestionAnswerDto[];
}

export interface TagScrapCollectionFeedbackDto {
  requiresFeedback: boolean;
  reason?: string;
}

export interface GlobalSettingsDto {
  id?: string;
  serviceCharge: number;
  isWaiveOffEnabled: boolean;
  minBillForWaiveOff: number;
  lastPriceUpdated: string | Date;
  bookingPeriodInDays: number;
  platformChargeInPercent: number;
  baseSubscriptionAmount: number;
  cgst: number;
  sgst: number;
  voucherCommissionPercentage: number;
  appointmentTimeSlots: string[];
  allowedLeavesPerMonth: number;
  informedLeavePenaltyAmount: number;
  uninformedLeavePenaltyAmount: number;
  maxPastDaysForLedgerEntry?: number;
  feedbackHighValueThreshold?: number;
  feedbackLowValueThreshold?: number;
  feedbackSeniorAgentAgeMonths?: number;
  feedbackSeniorAgentFrequency?: number;
  feedbackNewAgentAgeMonths?: number;
  feedbackQuestions?: FeedbackQuestionDefinitionDto[];
}

export interface UpdateGlobalSettingsDto {
  serviceCharge: number;
  isWaiveOffEnabled: boolean;
  minBillForWaiveOff: number;
  bookingPeriodInDays: number;
  platformChargeInPercent: number;
  baseSubscriptionAmount: number;
  cgst: number;
  sgst: number;
  voucherCommissionPercentage: number;
  appointmentTimeSlots: string[];
  allowedLeavesPerMonth: number;
  informedLeavePenaltyAmount: number;
  uninformedLeavePenaltyAmount: number;
  maxPastDaysForLedgerEntry?: number;
  feedbackHighValueThreshold?: number;
  feedbackLowValueThreshold?: number;
  feedbackSeniorAgentAgeMonths?: number;
  feedbackSeniorAgentFrequency?: number;
  feedbackNewAgentAgeMonths?: number;
  feedbackQuestions?: FeedbackQuestionDefinitionDto[];
}

export interface ScrapItemPriceDto {
  scrapItemId: string;
  regularPrice: number;
  specialPrice: number;
  scrapCategory: string;
  scrapCategoryId?: string | null;
  scrapName: string;
  unit: string;
  imageName: string;
  isActive: boolean;
  isFeatured: boolean;
  dealerPercentage: number;
  customerPrice: number;
  isScrap: boolean;
  isVisibleToCustomer: boolean;
  sortOrder: number;
  alternativeNames?: string[] | null;
}

export interface AgentAppConsolidatedDataDto {
  scrapItemPriceList: ScrapItemPriceDto[];
  globalSettings: GlobalSettingsDto;
}

export interface ScrapItemInsight {
  scrapItemId: string;
  scrapItemName: string;
  unit: string;
  totalQuantity: number;
  totalDeductedQuantity: number;
  netQuantity: number;
  totalValue: number;
  collectionCount: number;
}

export interface ScrapCollectionInsights {
  totalCollections: number;
  totalGrandTotal: number;
  totalPlatformCharges: number;
  totalCollectionCharges: number;
  totalScrapValue: number;
  totalNonScrapValue: number;
  totalVoucherCommission: number;
  totalAgentVoucherCommission: number;
  totalLeadGenerationFee?: number;
  scrapItemInsights: ScrapItemInsight[];
}

// Updated interfaces to match new backend schema
export interface CreateAppointment {
  customerId?: string;
  date: string; // ISO string
  address: string;
  addressId?: string;
  location: location;
  locationName: string;
  geoZoneId: string;
  pinCode: string;
  remarks?: string;
  timeSlot?: string;
  appointmentId?: string; // For reschedule
}

export interface CreateScrapCollection {
  customerId: string;
  appointmentId: string;
  agentId: string;
  date: string; // ISO string
  address: string;
  location: location;
  locationName: string;
  geoZoneId: string;
  collectionDetails: ScrapCollectionDetails[];
}

export interface ScrapCollection {
  id: string;
  customerId: string;
  customerName: string;
  customerPhoneNumber: string;
  appointmentId: string;
  date: string; // ISO string
  address: string;
  location: location;
  locationName: string;
  geoZoneId: string;
  totalItems: number;
  totalPrice: number;
  totalScrapPrice: number;
  totalNonScrapPrice: number;
  collectionCharges: number;
  platformCharges: number;
  grandTotal: number;
  agentDetails: AgentDetails;
  collectionDetails: ScrapCollectionDetails[];
  collectionId?: string;
  upiDeepLink?: string;
  isValid?: boolean;
  downloadCollectionReport?: string;
  paymentMethod?: number | string;
  voucherSettlementStatus?: VoucherSettlementStatus;
  voucherSettlementNotes?: string;
  voucherSettlementDate?: string | null;
  voucherCommission?: number | null;
  agentVoucherCommission?: number | null;
  requiresFeedback?: boolean;
  feedbackDetails?: ScrapCollectionFeedbackDetailsDto | null;
}

export enum VoucherSettlementStatus {
  Pending = 0,
  Settled = 1,
  Rejected = 2,
}

export interface BulkUpdateVoucherSettlementDto {
  scrapCollectionIds: string[];
  status: VoucherSettlementStatus;
  notes?: string;
  dateOfCollection?: string;
  idempotencyKey?: string;
}

export interface ScrapCollectionDetails {
  scrapItemId: string;
  scrapItemName: string;
  unit: string;
  unitPrice: number;
  marketPrice?: number;
  quantity: number;
  totalPrice: number;
  deductedQuantity: number;
}

export interface AgentDetails {
  agentId: string;
  name: string;
  phoneNumber: string;
  vehicleNumber: string;
  customerFeedback: string;
  agentRating: number;
}

export interface location {
  latitude: number;
  longitude: number;
}

export interface CustomerAddress {
  addressId: string;
  address: string;
  pincode: string;
  isPrimary: boolean;
  location: location;
  locationName: string;
  addressType?: string | null;
}

export interface Customer {
  id: string;
  customerId: string;
  phoneNumber: string;
  customerAddress?: CustomerAddress[];
  name: string;
  collectionCharge?: number | null;
  platformFeeInPercentage?: number | null;
  totalCollections?: number;
  totalCollectionAmount?: number;
  lastCollectionDate?: string | null;
}

// Subscription interfaces - Updated to match new SubscriptionDto
export interface Subscription {
  // Basic Subscription Information
  id: string;
  subscriptionId: string;
  agentId: string;
  zoneId: string;
  zoneName: string;
  status: number; // SubscriptionStatus
  type: number; // SubscriptionType
  cadence: number; // SubscriptionCadence
  startDate: string;
  endDate: string;
  createdDate: string;
  updatedDate: string | null;

  // Payment Information
  baseAmount: number;
  discountPercentage: number;
  discountAmount: number;
  amount: number;
  currency: string;
  paymentStatus: number; // PaymentStatus
  paymentMethod: number; // PaymentMethod
  paymentDate: string;
  paymentReference: string | null;
  notes: string | null;
  verifiedBy: string | null;

  // Tax Information
  sgst: number;
  cgst: number;
  igst: number;

  // Calculated properties for GST
  totalGST: number;
  amountWithGST: number;
  gstBreakdown: string;
}

export interface CreateSubscription {
  agentId: string;
  zoneId: string;
  type: number; // SubscriptionType
  cadence: number; // SubscriptionCadence
  paymentMethod: number; // PaymentMethod
  paymentReference: string | null;
  notes: string | null;
  verifiedBy: string | null;
}

export interface UpdateSubscription {
  subscriptionId: string;
  type: number; // SubscriptionType
  cadence: number; // SubscriptionCadence
  status: number; // SubscriptionStatus
}

export interface SubscriptionLimits {
  zoneId: string;
  zoneName: string;
  maxAgents: number;
  currentAgents: number;
  availableSlots: number;
}

export interface SubscriptionAccess {
  hasAccess: boolean;
  message: string;
  subscriptionDetails?: Subscription;
}

export interface Agent {
  id: string;
  agentId: string;
  name: string;
  phoneNumber: string;
  zones: string[];
  isActive: boolean;
}

export interface ZoneServiceDay {
  /** 0 = Sunday … 6 = Saturday (System.DayOfWeek). */
  day: number;
  /** Slots offered that day; null/empty = every global slot. */
  timeSlots?: string[] | null;
}

export interface UpsertZoneDto {
  id?: string;
  name: string;
  coordinates: { latitude: number; longitude: number }[];
  maxAppointmentsPerDay?: number | null;
  maxAppointmentsPerSlot?: number | null;
  pricingGroupId?: string | null;
  districtId?: string | null;
  serviceSchedule?: ZoneServiceDay[] | null;
}

/** An active appointment returned with a 409 when a zone/holiday change would strand it. */
export interface BlockingAppointment {
  id: string;
  date: string;
  timeSlot?: string | null;
  customerName?: string | null;
  status?: string | null;
  geoZoneId?: string | null;
}

export interface BookingConflict {
  message: string;
  totalCount: number;
  appointments: BlockingAppointment[];
}

/** Reads a BookingConflictException (409 problem details) from an HttpErrorResponse, if it is one. */
export function readBookingConflict(error: any): BookingConflict | null {
  if (error?.status !== 409 || !error?.error?.blockingAppointments) {
    return null;
  }
  return {
    message: error.error.detail ?? 'This change conflicts with existing appointments.',
    totalCount: error.error.blockingAppointmentCount ?? error.error.blockingAppointments.length,
    appointments: error.error.blockingAppointments,
  };
}

export interface Zone {
  id: string;
  name: string;
  disabled?: string;
  deletedAt?: string;
  updatedAt: string;
  createdAt: string;
  maxAppointmentsPerDay?: number | null;
  maxAppointmentsPerSlot?: number | null;
  pricingGroupId?: string | null;
  districtId?: string | null;
  serviceSchedule?: ZoneServiceDay[] | null;
  coordinate?: {
    type: string;
    coordinates: {
      latitude: number;
      longitude: number;
    }[];
  };
}

export enum PricingType {
  Percentage = 0,
  Fixed = 1
}

export interface PricingGroupItem {
  scrapItemId: string;
  scrapItemName?: string;
  pricingType: PricingType;
  value: number;
  dealerPercentage?: number | null;
  isActive?: boolean | null;
  isVisibleToCustomer?: boolean | null;
}

export interface PricingGroup {
  id: string;
  name: string;
  items: PricingGroupItem[];
}

export interface CreatePricingGroup {
  name: string;
  items: PricingGroupItem[];
}

export interface PlatformChargeTransaction {
  id: string;
  scrapCollectionId: string;
  agentId: string;
  agentName: string;
  geoZoneId: string;
  platformChargeInPercentage: number;
  platformChargeAmount: number;
  cgst: number;
  sgst: number;
  collectionDate: string;
  remittanceDate: string | null;
  remittanceMedium: string | null;
  remittanceNotes: string | null;
  createdDate: string;
  updatedDate: string | null;
}

export interface BulkRemittanceUpdateRequest {
  platformChargeTransactionIds: string[];
  remittanceDate: string;
  remittanceMedium: string;
  remittanceNotes: string;
}

export interface UnsupportedLocation {
  id: string;
  userId: string;
  phoneNumber: string;
  date: string;
  latitude: number;
  longitude: number;
  location: location;
}

export interface UnsupportedLocationRemarkDto {
  status: string;
  comment: string;
  createdBy: string;
  createdAt: string;
}

export interface UnsupportedLocationDto {
  id: string;
  userId: string;
  phoneNumber: string;
  name: string;
  date: string;
  latitude: number;
  longitude: number;
  location: location;
  status: string;
  remarks: UnsupportedLocationRemarkDto[];
}

export interface UnsupportedLocationDtoPaginatedResponse {
  items: UnsupportedLocationDto[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
}

export interface UpdateUnsupportedLocationStatusRequestDto {
  status: string;
  remark?: string;
}

export interface MarketingPlaceRemarkDto {
  status: string;
  comment: string;
  createdBy: string;
  createdAt: string;
}

export interface GooglePlaceTypeDto {
  name: string;
  displayName: string;
  category: string;
}

export interface MarketingPlaceDto {
  placeId: string;
  name: string;
  address: string;
  phoneNumber: string;
  website: string;
  latitude: number;
  longitude: number;
  types: string[];
  lastSyncedAt: string;
  status: string;
  remarks: MarketingPlaceRemarkDto[];
}

export interface MarketingPlaceDtoPaginatedResponse {
  items: MarketingPlaceDto[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
}

export interface UpdateMarketingLeadStatusRequestDto {
  status: string;
  remark?: string;
}

export enum MarketingPlaceJobStatus {
  Pending = 'Pending',
  Running = 'Running',
  Completed = 'Completed',
  Failed = 'Failed',
  Cancelled = 'Cancelled',
}

export interface CollectMarketingPlacesRequestDto {
  query?: string;
  placeType?: string;
  latitude?: number;
  longitude?: number;
  radiusMeters?: number;
  fetchAllPages?: boolean;
  forceRefresh?: boolean;
}

export interface MarketingPlaceJobStatusDto {
  id: string;
  query?: string;
  placeType?: string;
  latitude?: number;
  longitude?: number;
  radiusMeters?: number;
  fetchAllPages?: boolean;
  status: MarketingPlaceJobStatus;
  statusMessage?: string;
  pagesProcessed?: number;
  businessesFetched?: number;
  businessesMutated?: number;
  businessesSkipped?: number;
  createdAt?: string;
  updatedAt?: string;
  completedAt?: string;
  expiresAt?: string;
}

export interface ScrapCategoryDto {
  id: string;
  name: string;
}

export interface NonScrapItemDetailDto {
  scrapItemId: string;
  scrapItemName: string;
  unit: string;
  quantity: number;
  deductedQuantity: number;
  netQuantity: number;
  unitPrice: number;
  totalPrice: number;
}

export interface NonScrapItemTransactionDto {
  id: string;
  scrapCollectionId: string;
  agentId: string;
  agentName: string;
  geoZoneId: string;
  totalNonScrapPrice: number;
  collectionDate: string;
  isSettled: boolean;
  settlementDate: string | null;
  settlementMedium: string | null;
  settlementNotes: string | null;
  nonScrapItems: NonScrapItemDetailDto[];
  createdDate: string;
  updatedDate: string | null;
}

export interface BulkSettlementUpdateRequest {
  nonScrapItemTransactionIds: string[];
  settlementDate: string;
  settlementMedium: string;
  settlementNotes: string;
}

export enum VoucherStatus {
  Active = 'Active',
  Redeemed = 'Redeemed',
  Expired = 'Expired',
  Cancelled = 'Cancelled',
}

export interface VoucherItemContributionDto {
  scrapItemId: string;
  scrapItemName: string;
  multiplier: number;
  contributionAmount: number;
}

export interface VoucherDto {
  id: string;
  voucherConfigId?: string | null;
  voucherConfigName?: string | null;
  customerId: string;
  customerName: string;
  customerPhoneNumber: string;
  scrapCollectionId: string;
  voucherAmount: number;
  minimumPurchaseAmount: number;
  partnerId: string;
  partnerName: string;
  partnerLogo?: string | null;
  voucherCode: string;
  expiryDate: string;
  status: VoucherStatus;
  statusString: string;
  outletId?: string | null;
  outletName?: string | null;
  partnerUserId?: string | null;
  partnerUserName?: string | null;
  redeemedAt?: string | null;
  redemptionReference?: string | null;
  notes?: string | null;
  itemContributions: VoucherItemContributionDto[];
}

export interface CreateVoucherDto {
  voucherConfigId?: string | null;
  customerId: string;
  customerName: string;
  customerPhoneNumber: string;
  scrapCollectionId: string;
  voucherAmount: number;
  minimumPurchaseAmount: number;
  partnerId: string;
  partnerName: string;
  partnerLogo?: string | null;
  expiryDate: string;
  notes?: string | null;
}

export interface ClaimVouchersDto {
  voucherIds: string[];
  redemptionReference?: string | null;
  notes?: string | null;
  billAmount: number;
}

export interface UpdateVoucherStatusDto {
  id: string;
  status: VoucherStatus;
  redemptionReference?: string | null;
  notes?: string | null;
}

export interface RevokeVouchersDto {
  customerId: string;
  redemptionReference: string;
  notes?: string | null;
}

export interface VoucherFilterDto {
  phoneNumber?: string | null;
  customerName?: string | null;
  partnerName?: string | null;
  minVoucherAmount?: number | null;
  maxVoucherAmount?: number | null;
  partnerId?: string | null;
  outletId?: string | null;
  status?: number | null; // Numeric status: 0=Active, 1=Redeemed, 2=Expired, 3=Cancelled
  redemptionReference?: string | null;
  fromDate?: string | null;
  toDate?: string | null;
  pageIndex: number;
  pageSize: number;
}

export interface Holiday {
  id: string;
  name: string;
  type: string;
  date: string;
  geoZoneId: string | null;
  timeSlot: string | null;
}

export interface CreateHolidayDto {
  name: string;
  type: string;
  date: string;
  geoZoneId?: string | null;
  timeSlot?: string | null;
}

export interface HolidayFilter {
  fromDate?: string | null;
  toDate?: string | null;
  pageIndex?: number;
  pageSize?: number;
}

export interface PartnerDto {
  id: string;
  name: string;
  address: string;
  contactPerson: string;
  contactNumber: string;
  alternateContactNumber?: string | null;
  emailId?: string | null;
}

export interface CreatePartnerDto {
  name: string;
  address: string;
  contactPerson: string;
  contactNumber: string;
  alternateContactNumber?: string | null;
  emailId?: string | null;
}

export interface GenerateInvoicesResultDto {
  year: number;
  month: number;
  invoicesGenerated: number;
  billingPeriodStartUtc: string;
  billingPeriodEndUtc: string;
}

export interface PartnerInvoiceDto {
  invoiceId: string | null;
  partnerId: string | null;
  partnerName: string | null;
  invoiceDate: string;
  billingPeriodStart: string;
  billingPeriodEnd: string;
  amountDue: number;
  totalAmount: number;
  status: string | null;
  invoiceDownloadUrl: string | null;
  transactionCount: number;
  redemptionReferences: string[] | null;
  createdAt: string;
  updatedAt: string;
}

export interface VoucherZoneInfoDto {
  zoneId: string;
  zoneName: string;
}

export interface VoucherItemMultiplierDto {
  scrapItemId: string;
  scrapItemName: string;
  multiplier: number;
}

export interface VoucherConfigurationDto {
  id: string;
  name: string;
  partnerId: string;
  partnerName: string;
  partnerLogo?: string | null;
  validityPeriodInMonths: number;
  zones: VoucherZoneInfoDto[];
  possibleVoucherAmounts: number[];
  billAmountMultiplier: number;
  defaultMultiplier: number;
  itemMultipliers: VoucherItemMultiplierDto[];
}

export interface CreateVoucherConfigurationDto {
  name: string;
  partnerId: string;
  partnerName: string;
  partnerLogo?: string | null;
  validityPeriodInMonths: number;
  zones: VoucherZoneInfoDto[];
  possibleVoucherAmounts: number[];
  billAmountMultiplier: number;
  defaultMultiplier: number;
  itemMultipliers: VoucherItemMultiplierDto[];
}

export interface GeoZoneLaunchRecipientDto {
  phoneNumber: string;
  customerName: string;
}

export interface GeoZoneLaunchNotificationRequestDto {
  geoZoneId: string;
  whatsAppBodyParams?: string[];
  whatsAppTemplateName: string;
  whatsAppHeaderImageUrl?: string;
}

// WhatsApp Customer Care Queue — handed-off bot sessions awaiting a human telemarketer
export interface WhatsAppCareQueueItem {
  id: string;
  phoneNumber: string;
  language?: string | null;
  step: string;
  isCtwaFirstTouch: boolean;
  isHandedOff: boolean;
  handoffReason?: string | null;
  handoffAt?: string | null;
  lastInboundAt: string;
  isAttended: boolean;
  attendedAt?: string | null;
  attendedBy?: string | null;
}

export interface WhatsAppCareQueueFilterDto {
  pageIndex: number;
  pageSize: number;
  includeAttended: boolean;
  phoneNumber?: string;
}

export interface WhatsAppCareQueuePaginatedResponse {
  items: WhatsAppCareQueueItem[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
}

// Returned immediately when a geo-zone launch notification is accepted for async sending — the
// actual sends happen afterward via Service Bus, not within this request/response cycle. Counts are
// a preview of scope computed at request time, not a completion report.
export interface GeoZoneLaunchNotificationJobAcceptedDto {
  campaignId: string;
  matchedRecipientCount: number;
  alreadyNotifiedCount: number;
  optedOutCount: number;
  queuedCount: number;
}

// One row of a geo-zone launch campaign's send log — who was messaged, whether it succeeded, and
// why not if it failed. Populated asynchronously by the Function that actually sends the messages,
// so this may lag behind QueuedCount immediately after launching.
export interface WhatsAppCampaignSendLogEntryDto {
  campaignId: string; // e.g. "NoSignupWinBack" or "GeoZoneLaunch:{geoZoneId}"
  phoneNumber: string;
  status: string; // "Sent" | "Failed"
  sentAt: string;
  templateName: string;
  errorMessage?: string | null;
}

export interface WhatsAppCampaignSendLogPaginatedResponse {
  items: WhatsAppCampaignSendLogEntryDto[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
}

@Injectable({
  providedIn: 'root',
})
export class ExternalDataService implements OnDestroy {
  private readonly _destroying$ = new Subject<void>();
  private agentConsolidatedData =
    new BehaviorSubject<AgentAppConsolidatedDataDto | null>(null);
  agentConsolidatedData$ = this.agentConsolidatedData.asObservable();

  constructor(
    private http: HttpClient,
    private authService: AuthService,
  ) { }

  private getVerifiedByEmail(): string {
    const activeAccount = this.authService.getActiveAccount();
    const idTokenClaims = (activeAccount as any)?.idTokenClaims;
    const emailFromClaims =
      idTokenClaims?.email ||
      idTokenClaims?.preferred_username ||
      idTokenClaims?.upn;

    return (
      activeAccount?.username ||
      emailFromClaims ||
      activeAccount?.localAccountId ||
      activeAccount?.homeAccountId?.split('.')[0] ||
      activeAccount?.name ||
      'Unknown'
    );
  }

  getAppointments(
    pageIndex: number,
    pageSize: number,
    geoZoneId?: string,
  ): Observable<{ items: Appointment[]; totalCount: number }> {
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);
    const body: any = {
      date: currentDate.toISOString(),
      pageIndex: pageIndex - 1,
      pageSize: pageSize,
    };

    if (geoZoneId) {
      body.geoZoneId = geoZoneId;
    }

    return this.http
      .post<{
        items: Appointment[];
        totalCount: number;
      }>(`${environment.apiConfig.uri}/api/Appointment/admin`, body)
      .pipe(map((res) => ({ items: res.items, totalCount: res.totalCount })));
  }

  getPastAppointments(
    pageIndex: number,
    pageSize: number,
    geoZoneId?: string,
  ): Observable<{ items: Appointment[]; totalCount: number }> {
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);
    const body: any = {
      date: currentDate.toISOString(),
      pageIndex: pageIndex - 1,
      pageSize: pageSize,
    };

    if (geoZoneId) {
      body.geoZoneId = geoZoneId;
    }

    return this.http
      .post<{
        items: Appointment[];
        totalCount: number;
      }>(`${environment.apiConfig.uri}/api/Appointment/history/admin`, body)
      .pipe(map((res) => ({ items: res.items, totalCount: res.totalCount })));
  }

  getFutureAppointments(
    pageIndex: number,
    pageSize: number,
    geoZoneId?: string,
  ): Observable<{ items: Appointment[]; totalCount: number }> {
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);
    const body: any = {
      date: currentDate.toISOString(),
      pageIndex: pageIndex - 1,
      pageSize: pageSize,
    };

    if (geoZoneId) {
      body.geoZoneId = geoZoneId;
    }

    return this.http
      .post<{
        items: Appointment[];
        totalCount: number;
      }>(`${environment.apiConfig.uri}/api/Appointment/future/admin`, body)
      .pipe(map((res) => ({ items: res.items, totalCount: res.totalCount })));
  }

  getAppointmentsWithFilter(filters: {
    pageIndex?: number;
    pageSize?: number;
    dateFrom?: string;
    dateTo?: string;
    status?: string;
    geoZoneIds?: string;
    agentIds?: string;
    customerName?: string;
    phoneNumber?: string;
  }): Observable<{ items: Appointment[]; totalCount: number }> {
    const body: any = {
      pageIndex: filters.pageIndex ?? 0,
      pageSize: filters.pageSize ?? 50,
    };

    if (filters.dateFrom) {
      body.dateFrom = filters.dateFrom;
    }
    if (filters.dateTo) {
      body.dateTo = filters.dateTo;
    }
    if (filters.status) {
      body.status = filters.status;
    }
    if (filters.geoZoneIds) {
      body.geoZoneIds = filters.geoZoneIds;
    }
    if (filters.agentIds) {
      body.agentIds = filters.agentIds;
    }
    if (filters.customerName) {
      body.customerName = filters.customerName;
    }
    if (filters.phoneNumber) {
      body.phoneNumber = filters.phoneNumber;
    }

    return this.http
      .post<{
        items: Appointment[];
        totalCount: number;
      }>(`${environment.apiConfig.uri}/api/Appointment/admin/filter`, body)
      .pipe(map((res) => ({ items: res.items, totalCount: res.totalCount })));
  }

  fetchAndStoreConsolidatedData() {
    this.http
      .get<AgentAppConsolidatedDataDto>(
        `${environment.apiConfig.uri}/api/home/agent-app`,
      )
      .subscribe((data) => {
        this.agentConsolidatedData.next(data);
      });
  }

  rejectAppointment(id: string, remarks: string): Observable<any> {
    return this.http.patch(
      `${environment.apiConfig.uri}/api/Appointment/${id}/reject?remarks=${encodeURIComponent(remarks)}`,
      {},
    );
  }

  confirmAppointment(id: string, remarks: string): Observable<any> {
    return this.http.patch(
      `${environment.apiConfig.uri}/api/Appointment/${id}/confirm?remarks=${encodeURIComponent(remarks)}`,
      {},
    );
  }

  updateAppointmentRequirements(id: string, requirements: any): Observable<any> {
    return this.http.patch(
      `${environment.apiConfig.uri}/api/Appointment/${id}/requirements`,
      requirements,
    );
  }

  cancelAppointment(appointmentId: string, remarks?: string): Observable<any> {
    let url = `${environment.apiConfig.uri}/api/Appointment/${appointmentId}/cancel`;
    if (remarks) {
      url += `?remarks=${encodeURIComponent(remarks)}`;
    }
    return this.http.patch(url, {});
  }

  rescheduleAppointment(appointment: CreateAppointment): Observable<any> {
    return this.http.post(
      `${environment.apiConfig.uri}/api/Appointment/reschedule`,
      appointment,
    );
  }

  getActiveAgents(): Observable<Agent[]> {
    return this.http
      .get<
        Agent[]
      >(`${environment.apiConfig.uri}/api/Agent/list?IsActive=true&pageSize=100`)
      .pipe(
        map((response) =>
          Array.isArray(response) ? response : (response as any).items || [],
        ),
      );
  }

  assignAgentToAppointment(
    appointmentId: string,
    agentId: string,
  ): Observable<any> {
    return this.http.patch(
      `${environment.apiConfig.uri}/api/Appointment/${appointmentId}/assign-agent`,
      { agentId },
    );
  }

  createAppointment(
    customerId: string,
    appointmentData: CreateAppointment,
  ): Observable<Appointment> {
    appointmentData.customerId = customerId;
    return this.http.post<Appointment>(
      `${environment.apiConfig.uri}/api/Appointment/create`,
      appointmentData,
    );
  }

  getAppointmentById(appointmentId: string): Observable<Appointment[]> {
    return this.http.get<Appointment[]>(
      `${environment.apiConfig.uri}/api/Appointment/${appointmentId}`,
    );
  }

  getRecurringSchedulesActive(): Observable<RecurringAppointmentSchedule[]> {
    return this.http.get<RecurringAppointmentSchedule[]>(
      `${environment.apiConfig.uri}/api/RecurringAppointmentSchedule/active`,
    );
  }

  getRecurringSchedulesByCustomer(
    customerId: string,
    pageIndex: number = 0,
    pageSize: number = 20,
  ): Observable<{ items: RecurringAppointmentSchedule[]; totalCount: number }> {
    return this.http.get<{
      items: RecurringAppointmentSchedule[];
      totalCount: number;
    }>(
      `${environment.apiConfig.uri}/api/RecurringAppointmentSchedule/customer/${customerId}?pageIndex=${pageIndex}&pageSize=${pageSize}`,
    );
  }

  createRecurringSchedule(payload: {
    customerId: string;
    addressId: string;
    recurringInfo: RecurringInfo;
    name?: string;
    phoneNumber?: string;
  }): Observable<RecurringAppointmentSchedule> {
    return this.http.post<RecurringAppointmentSchedule>(
      `${environment.apiConfig.uri}/api/RecurringAppointmentSchedule/create`,
      payload,
    );
  }

  updateRecurringSchedule(
    id: string,
    payload: Partial<{
      addressId: string;
      recurringInfo: RecurringInfo;
      isActive: boolean;
    }>,
  ): Observable<RecurringAppointmentSchedule> {
    return this.http.put<RecurringAppointmentSchedule>(
      `${environment.apiConfig.uri}/api/RecurringAppointmentSchedule/${id}`,
      payload,
    );
  }

  deleteRecurringSchedule(id: string): Observable<any> {
    return this.http.delete(
      `${environment.apiConfig.uri}/api/RecurringAppointmentSchedule/${id}`,
    );
  }

  pauseRecurringSchedule(id: string): Observable<RecurringAppointmentSchedule> {
    return this.http.patch<RecurringAppointmentSchedule>(
      `${environment.apiConfig.uri}/api/RecurringAppointmentSchedule/${id}/pause`,
      {},
    );
  }

  resumeRecurringSchedule(
    id: string,
  ): Observable<RecurringAppointmentSchedule> {
    return this.http.patch<RecurringAppointmentSchedule>(
      `${environment.apiConfig.uri}/api/RecurringAppointmentSchedule/${id}/resume`,
      {},
    );
  }

  // Updated methods to use new interfaces
  submitScrapCollection(payload: CreateScrapCollection): Observable<any> {
    return this.http.post(
      `${environment.apiConfig.uri}/api/ScrapCollection/create`,
      payload,
    );
  }

  previewScrapCollection(
    payload: CreateScrapCollection,
  ): Observable<ScrapCollection> {
    return this.http.post<ScrapCollection>(
      `${environment.apiConfig.uri}/api/ScrapCollection/preview`,
      payload,
    );
  }

  getScrapCollection(id: string): Observable<ScrapCollection> {
    return this.http.get<ScrapCollection>(
      `${environment.apiConfig.uri}/api/ScrapCollection/${id}`,
    );
  }

  getScrapPriceList(): Observable<ScrapItemPriceDto[]> {
    return this.http.get<ScrapItemPriceDto[]>(
      `${environment.apiConfig.uri}/api/ScrapItem/price-list`,
    );
  }

  updateScrapPrices(
    prices: {
      scrapItemId: string;
      regularPrice: number;
      specialPrice: number;
      isActive: boolean;
      isFeatured: boolean;
      dealerPercentage: number;
      customerPrice: number;
      isScrap: boolean;
      isVisibleToCustomer: boolean;
      imageName: string;
      scrapName: string;
      unit: string;
      scrapCategory: string;
      scrapCategoryId?: string | null;
      alternativeNames?: string[] | null;
    }[],
  ): Observable<any> {
    return this.http.patch(
      `${environment.apiConfig.uri}/api/ScrapItem/update-bulk`,
      prices,
    );
  }

  createScrapItem(payload: {
    name: string;
    description: string;
    category: string;
    categoryId?: string | null;
    image: string;
    imageName?: string | null;
    unit: string;
    regularPrice: number;
    specialPrice: number;
    dealerPercentage?: number;
    isVisibleToCustomer?: boolean;
    isScrap?: boolean;
    alternativeNames?: string[] | null;
  }): Observable<any> {
    return this.http.post(
      `${environment.apiConfig.uri}/api/ScrapItem/create`,
      payload,
    );
  }

  deleteScrapItem(scrapItemId: string): Observable<any> {
    const body = { scrapItemId };
    return this.http.post(
      `${environment.apiConfig.uri}/api/ScrapItem/delete`,
      body,
    );
  }

  getScrapCategories(): Observable<ScrapCategoryDto[]> {
    return this.http.get<ScrapCategoryDto[]>(
      `${environment.apiConfig.uri}/api/ScrapCategory`,
    );
  }

  getCustomers(
    pageIndex: number = 0,
    pageSize: number = 10,
    filters?: {
      name?: string;
      phoneNumber?: string;
      hasUpiId?: boolean;
      addressText?: string;
      minCollections?: number;
      minCollectionAmount?: number;
      lastCollectionBefore?: string;
      lastCollectionAfter?: string;
    },
  ): Observable<{
    items: Customer[];
    totalCount: number;
    pageIndex: number;
    pageSize: number;
  }> {
    // Build query parameters
    let url = `${environment.apiConfig.uri}/api/Customer/all?pageIndex=${pageIndex}&pageSize=${pageSize}`;

    if (filters) {
      if (filters.name) {
        url += `&name=${encodeURIComponent(filters.name)}`;
      }
      if (filters.phoneNumber) {
        url += `&phoneNumber=${encodeURIComponent(filters.phoneNumber)}`;
      }
      if (filters.hasUpiId !== undefined) {
        url += `&hasUpiId=${filters.hasUpiId}`;
      }
      if (filters.addressText) {
        url += `&addressText=${encodeURIComponent(filters.addressText)}`;
      }
      if (filters.minCollections !== undefined && filters.minCollections !== null) {
        url += `&minCollections=${filters.minCollections}`;
      }
      if (filters.minCollectionAmount !== undefined && filters.minCollectionAmount !== null) {
        url += `&minCollectionAmount=${filters.minCollectionAmount}`;
      }
      if (filters.lastCollectionBefore) {
        url += `&lastCollectionBefore=${encodeURIComponent(filters.lastCollectionBefore)}`;
      }
      if (filters.lastCollectionAfter) {
        url += `&lastCollectionAfter=${encodeURIComponent(filters.lastCollectionAfter)}`;
      }
    }

    // Ensure MSAL is initialized before making the request
    if (!this.authService.isMsalInitialized()) {
      return new Observable((observer) => {
        this.authService
          .getMsalInitializedObservable()
          .pipe(
            filter((initialized) => initialized),
            take(1),
          )
          .subscribe(() => {
            this.http
              .get<{
                items: Customer[];
                totalCount: number;
                pageIndex: number;
                pageSize: number;
              }>(url)
              .subscribe(observer);
          });
      });
    }
    return this.http.get<{
      items: Customer[];
      totalCount: number;
      pageIndex: number;
      pageSize: number;
    }>(url);
  }

  deleteCustomer(phoneNumber: string): Observable<any> {
    const url = `${environment.apiConfig.uri}/api/Customer/admin/delete/${encodeURIComponent(phoneNumber)}`;
    return this.http.delete(url);
  }

  getCustomerById(customerId: string): Observable<Customer> {
    const url = `${environment.apiConfig.uri}/api/Customer/${customerId}`;

    if (!this.authService.isMsalInitialized()) {
      return new Observable((observer) => {
        this.authService
          .getMsalInitializedObservable()
          .pipe(
            filter((initialized) => initialized),
            take(1),
          )
          .subscribe(() => {
            this.http.get<Customer>(url).subscribe(observer);
          });
      });
    }

    return this.http.get<Customer>(url);
  }

  createCustomer(customerData: {
    name: string;
    phoneNumber: string;
    customerAddress: {
      address: string;
      pincode: string;
      locationName: string;
      isPrimary: boolean;
      location: location;
    }[];
  }): Observable<Customer> {
    // Ensure MSAL is initialized before making the request
    if (!this.authService.isMsalInitialized()) {
      return new Observable((observer) => {
        this.authService
          .getMsalInitializedObservable()
          .pipe(
            filter((initialized) => initialized),
            take(1),
          )
          .subscribe(() => {
            this.http
              .post<Customer>(
                `${environment.apiConfig.uri}/api/identity/createcustomer`,
                customerData,
              )
              .subscribe(observer);
          });
      });
    }
    return this.http.post<Customer>(
      `${environment.apiConfig.uri}/api/identity/createcustomer`,
      customerData,
    );
  }

  createCustomerAddress(addressData: {
    customerId: string;
    address: string;
    pincode: string;
    location: location;
    locationName: string;
    isPrimary: boolean;
    addressType?: string | null;
  }): Observable<any> {
    // Ensure MSAL is initialized before making the request
    if (!this.authService.isMsalInitialized()) {
      return new Observable((observer) => {
        this.authService
          .getMsalInitializedObservable()
          .pipe(
            filter((initialized) => initialized),
            take(1),
          )
          .subscribe(() => {
            this.http
              .post<any>(
                `${environment.apiConfig.uri}/api/customer/address/create`,
                addressData,
              )
              .subscribe(observer);
          });
      });
    }
    return this.http.post<any>(
      `${environment.apiConfig.uri}/api/customer/address/create`,
      addressData,
    );
  }

  updateCustomerAddress(addressData: {
    addressId: string;
    address: string;
    pincode: string;
    location: location;
    locationName: string;
    isPrimary: boolean;
    addressType?: string | null;
  }): Observable<any> {
    // Ensure MSAL is initialized before making the request
    if (!this.authService.isMsalInitialized()) {
      return new Observable((observer) => {
        this.authService
          .getMsalInitializedObservable()
          .pipe(
            filter((initialized) => initialized),
            take(1),
          )
          .subscribe(() => {
            this.http
              .post<any>(
                `${environment.apiConfig.uri}/api/customer/address/update`,
                addressData,
              )
              .subscribe(observer);
          });
      });
    }
    return this.http.post<any>(
      `${environment.apiConfig.uri}/api/customer/address/update`,
      addressData,
    );
  }

  markAddressAsPrimary(addressId: string, userId: string): Observable<any> {
    const requestBody = {
      addressId: addressId,
      userId: userId,
    };

    // Ensure MSAL is initialized before making the request
    if (!this.authService.isMsalInitialized()) {
      return new Observable((observer) => {
        this.authService
          .getMsalInitializedObservable()
          .pipe(
            filter((initialized) => initialized),
            take(1),
          )
          .subscribe(() => {
            this.http
              .post<any>(
                `${environment.apiConfig.uri}/api/Customer/address/mark-primary`,
                requestBody,
              )
              .subscribe(observer);
          });
      });
    }
    return this.http.post<any>(
      `${environment.apiConfig.uri}/api/Customer/address/mark-primary`,
      requestBody,
    );
  }

  deleteAddress(addressId: string, userId: string): Observable<any> {
    const requestBody = {
      addressId: addressId,
      userId: userId,
    };

    // Ensure MSAL is initialized before making the request
    if (!this.authService.isMsalInitialized()) {
      return new Observable((observer) => {
        this.authService
          .getMsalInitializedObservable()
          .pipe(
            filter((initialized) => initialized),
            take(1),
          )
          .subscribe(() => {
            this.http
              .post<any>(
                `${environment.apiConfig.uri}/api/Customer/address/delete`,
                requestBody,
              )
              .subscribe(observer);
          });
      });
    }
    return this.http.post<any>(
      `${environment.apiConfig.uri}/api/Customer/address/delete`,
      requestBody,
    );
  }

  updateCustomerFees(
    customerId: string,
    collectionCharge: number | null,
    platformFeeInPercentage: number | null,
  ): Observable<boolean> {
    const requestBody = {
      customerId: customerId,
      collectionCharge: collectionCharge,
      platformFeeInPercentage: platformFeeInPercentage,
    };

    // Ensure MSAL is initialized before making the request
    if (!this.authService.isMsalInitialized()) {
      return new Observable((observer) => {
        this.authService
          .getMsalInitializedObservable()
          .pipe(
            filter((initialized) => initialized),
            take(1),
          )
          .subscribe(() => {
            this.http
              .post<boolean>(
                `${environment.apiConfig.uri}/api/Customer/${customerId}/fees`,
                requestBody,
              )
              .subscribe(observer);
          });
      });
    }
    return this.http.post<boolean>(
      `${environment.apiConfig.uri}/api/Customer/${customerId}/fees`,
      requestBody,
    );
  }

  updateAppointmentFees(
    appointmentId: string,
    collectionCharge: number | null,
    platformFeeInPercentage: number | null,
  ): Observable<boolean> {
    const requestBody = {
      appointmentId: appointmentId,
      collectionCharge: collectionCharge,
      platformFeeInPercentage: platformFeeInPercentage,
    };

    // Ensure MSAL is initialized before making the request
    if (!this.authService.isMsalInitialized()) {
      return new Observable((observer) => {
        this.authService
          .getMsalInitializedObservable()
          .pipe(
            filter((initialized) => initialized),
            take(1),
          )
          .subscribe(() => {
            this.http
              .patch<boolean>(
                `${environment.apiConfig.uri}/api/Appointment/${appointmentId}/fees`,
                requestBody,
              )
              .subscribe(observer);
          });
      });
    }
    return this.http.patch<boolean>(
      `${environment.apiConfig.uri}/api/Appointment/${appointmentId}/fees`,
      requestBody,
    );
  }

  // Subscription methods
  getAllSubscriptions(
    pageIndex: number = 0,
    pageSize: number = 10,
  ): Observable<{
    items: Subscription[];
    totalCount: number;
    pageIndex: number;
    pageSize: number;
  }> {
    if (!this.authService.isMsalInitialized()) {
      return new Observable((observer) => {
        this.authService
          .getMsalInitializedObservable()
          .pipe(
            filter((initialized) => initialized),
            take(1),
          )
          .subscribe(() => {
            this.http
              .get<Subscription[]>(
                `${environment.apiConfig.uri}/api/Subscription/all`,
              )
              .pipe(
                map((subscriptions) => ({
                  items: subscriptions,
                  totalCount: subscriptions.length,
                  pageIndex: pageIndex,
                  pageSize: pageSize,
                })),
              )
              .subscribe(observer);
          });
      });
    }
    return this.http
      .get<Subscription[]>(`${environment.apiConfig.uri}/api/Subscription/all`)
      .pipe(
        map((subscriptions) => ({
          items: subscriptions,
          totalCount: subscriptions.length,
          pageIndex: pageIndex,
          pageSize: pageSize,
        })),
      );
  }

  getSubscriptionById(subscriptionId: string): Observable<Subscription> {
    return this.http.get<Subscription>(
      `${environment.apiConfig.uri}/api/Subscription/${subscriptionId}`,
    );
  }

  getSubscriptionsByAgent(agentId: string): Observable<Subscription[]> {
    return this.http.get<Subscription[]>(
      `${environment.apiConfig.uri}/api/Subscription/agent/${agentId}`,
    );
  }

  getActiveSubscriptionsByZone(zoneId: string): Observable<Subscription[]> {
    return this.http.get<Subscription[]>(
      `${environment.apiConfig.uri}/api/Subscription/zone/${zoneId}/active`,
    );
  }

  getSubscriptionLimits(zoneId: string): Observable<SubscriptionLimits> {
    return this.http.get<SubscriptionLimits>(
      `${environment.apiConfig.uri}/api/Subscription/zone/${zoneId}/limits`,
    );
  }

  checkSubscriptionAccess(): Observable<SubscriptionAccess> {
    return this.http.get<SubscriptionAccess>(
      `${environment.apiConfig.uri}/api/Subscription/check-access`,
    );
  }

  getExpiredSubscriptions(): Observable<Subscription[]> {
    return this.http.get<Subscription[]>(
      `${environment.apiConfig.uri}/api/Subscription/expired`,
    );
  }

  getSubscriptionsRequiringRenewal(): Observable<Subscription[]> {
    return this.http.get<Subscription[]>(
      `${environment.apiConfig.uri}/api/Subscription/requiring-renewal`,
    );
  }

  getPendingVerificationSubscriptions(): Observable<Subscription[]> {
    return this.http.get<Subscription[]>(
      `${environment.apiConfig.uri}/api/Subscription/pending-verification`,
    );
  }

  createSubscription(
    subscriptionData: CreateSubscription,
  ): Observable<Subscription> {
    // Format request body to match API expectations (PascalCase)
    const requestBody = {
      AgentId: subscriptionData.agentId,
      ZoneId: subscriptionData.zoneId,
      Type: subscriptionData.type,
      Cadence: subscriptionData.cadence,
      PaymentMethod: subscriptionData.paymentMethod,
      PaymentReference: subscriptionData.paymentReference,
      Notes: subscriptionData.notes,
      VerifiedBy: subscriptionData.verifiedBy,
    };

    return this.http
      .post<Subscription>(
        `${environment.apiConfig.uri}/api/Subscription/create`,
        requestBody,
      )
      .pipe(
        tap({
          error: (error: any) => {
            console.error('=== Create Subscription ERROR ===');
            console.error('Error Status:', error?.status);
            console.error('Error Status Text:', error?.statusText);
            console.error('Error URL:', error?.url);
            console.error('Error Message:', error?.message);
            console.error('Error Body:', error?.error);
            console.error('Full Error Object:', error);

            if (error?.error) {
              console.error(
                'Error Details JSON:',
                JSON.stringify(error.error, null, 2),
              );
            }

            if (error?.error?.errors) {
              console.error('Validation Errors:');
              Object.keys(error.error.errors).forEach((key: string) => {
                console.error(`  ${key}:`, error.error.errors[key]);
              });
            }
          },
        }),
      );
  }

  updateSubscription(updateData: UpdateSubscription): Observable<Subscription> {
    const requestBody = {
      SubscriptionId: updateData.subscriptionId,
      Type: updateData.type,
      Cadence: updateData.cadence,
      Status: updateData.status,
    };

    return this.http
      .put<Subscription>(
        `${environment.apiConfig.uri}/api/Subscription/update`,
        requestBody,
      )
      .pipe(
        tap({
          error: (error: any) => {
            console.error('=== Update Subscription ERROR ===');
            console.error('Error Status:', error?.status);
            console.error('Error Status Text:', error?.statusText);
            console.error('Error URL:', error?.url);
            console.error('Error Message:', error?.message);
            console.error('Error Body:', error?.error);
            console.error('Full Error Object:', error);

            if (error?.error) {
              console.error(
                'Error Details JSON:',
                JSON.stringify(error.error, null, 2),
              );
            }

            if (error?.error?.errors) {
              console.error('Validation Errors:');
              Object.keys(error.error.errors).forEach((key: string) => {
                console.error(`  ${key}:`, error.error.errors[key]);
              });
            }
          },
        }),
      );
  }

  // Agent and Zone methods
  getAllAgents(isActive?: boolean): Observable<Agent[]> {
    let url = `${environment.apiConfig.uri}/api/Agent/list?pageSize=100`;
    if (isActive !== undefined) {
      url += `&isActive=${isActive}`;
    }
    if (!this.authService.isMsalInitialized()) {
      return new Observable((observer) => {
        this.authService
          .getMsalInitializedObservable()
          .pipe(
            filter((initialized) => initialized),
            take(1),
          )
          .subscribe(() => {
            this.http
              .get<{
                items: Agent[];
                totalCount: number;
                pageNumber: number;
                pageSize: number;
              }>(url)
              .pipe(map((response) => response.items))
              .subscribe(observer);
          });
      });
    }
    return this.http
      .get<{
        items: Agent[];
        totalCount: number;
        pageNumber: number;
        pageSize: number;
      }>(url)
      .pipe(map((response) => response.items));
  }

  getAllZones(): Observable<Zone[]> {
    if (!this.authService.isMsalInitialized()) {
      return new Observable((observer) => {
        this.authService
          .getMsalInitializedObservable()
          .pipe(
            filter((initialized) => initialized),
            take(1),
          )
          .subscribe(() => {
            this.http
              .get<Zone[]>(`${environment.apiConfig.uri}/api/GeoZone/list`)
              .subscribe(observer);
          });
      });
    }
    return this.http.get<Zone[]>(
      `${environment.apiConfig.uri}/api/GeoZone/list`,
    );
  }

  createZone(zone: UpsertZoneDto): Observable<{ id: string }> {
    return this.http.post<{ id: string }>(
      `${environment.apiConfig.uri}/api/GeoZone`,
      zone,
    );
  }

  /** Enables or disables a zone; disabling returns 409 (see readBookingConflict) while it has active appointments. */
  setZoneStatus(id: string, enabled: boolean): Observable<Zone> {
    return this.http.patch<Zone>(
      `${environment.apiConfig.uri}/api/GeoZone/${encodeURIComponent(id)}/status`,
      { enabled },
    );
  }

  updateZone(id: string, zoneData: Partial<UpsertZoneDto>): Observable<any> {
    const payload = {
      id: id,
      ...zoneData,
    };
    return this.http.patch<any>(
      `${environment.apiConfig.uri}/api/GeoZone`,
      payload,
    );
  }

  getPricingGroups(): Observable<PricingGroup[]> {
    return this.http.get<PricingGroup[]>(
      `${environment.apiConfig.uri}/api/PricingGroup/list`
    );
  }

  getPricingGroup(id: string): Observable<PricingGroup> {
    return this.http.get<PricingGroup>(
      `${environment.apiConfig.uri}/api/PricingGroup/${id}`
    );
  }

  createPricingGroup(pricingGroup: CreatePricingGroup): Observable<any> {
    return this.http.post<any>(
      `${environment.apiConfig.uri}/api/PricingGroup/create`,
      pricingGroup
    );
  }

  updatePricingGroup(id: string, pricingGroup: CreatePricingGroup): Observable<any> {
    return this.http.put<any>(
      `${environment.apiConfig.uri}/api/PricingGroup/${id}`,
      pricingGroup
    );
  }

  deletePricingGroup(id: string): Observable<any> {
    return this.http.delete<any>(
      `${environment.apiConfig.uri}/api/PricingGroup/${id}`
    );
  }

  getPlatformChargeTransactions(
    pageIndex: number = 0,
    pageSize: number = 10,
    agentId?: string,
    geoZoneId?: string,
    hasRemittance?: boolean,
    fromDate?: string,
    toDate?: string,
  ): Observable<{
    items: PlatformChargeTransaction[];
    totalCount: number;
    totalAmount: number;
    pageIndex: number;
    pageSize: number;
  }> {
    let url = `${environment.apiConfig.uri}/api/PlatformChargeTransaction?pageIndex=${pageIndex}&pageSize=${pageSize}`;

    if (agentId) {
      url += `&agentId=${agentId}`;
    }
    if (geoZoneId) {
      url += `&geoZoneId=${geoZoneId}`;
    }
    if (hasRemittance !== undefined && typeof hasRemittance === 'boolean') {
      url += `&hasRemittance=${hasRemittance}`;
    }
    if (fromDate) {
      url += `&fromDate=${encodeURIComponent(fromDate)}`;
    }
    if (toDate) {
      url += `&toDate=${encodeURIComponent(toDate)}`;
    }

    if (!this.authService.isMsalInitialized()) {
      return new Observable((observer) => {
        this.authService
          .getMsalInitializedObservable()
          .pipe(
            filter((initialized) => initialized),
            take(1),
          )
          .subscribe(() => {
            this.http
              .get<{
                items: PlatformChargeTransaction[];
                totalCount: number;
                totalAmount: number;
                pageIndex: number;
                pageSize: number;
              }>(url)
              .subscribe(observer);
          });
      });
    }
    return this.http.get<{
      items: PlatformChargeTransaction[];
      totalCount: number;
      totalAmount: number;
      pageIndex: number;
      pageSize: number;
    }>(url);
  }

  getUnsupportedLocations(fromDateUtcIso: string, toDateUtcIso: string) {
    const url = `${environment.apiConfig.uri}/api/GeoZone/unsupported-locations/report?fromDate=${encodeURIComponent(fromDateUtcIso)}&toDate=${encodeURIComponent(toDateUtcIso)}`;
    return this.http.get<UnsupportedLocation[]>(url);
  }

  getUnsupportedLocationsByGeoZone(
    geoZoneId: string,
    status?: string,
    pageIndex: number = 0,
    pageSize: number = 10,
  ): Observable<UnsupportedLocationDtoPaginatedResponse> {
    let url = `${environment.apiConfig.uri}/api/GeoZone/unsupported-locations/by-geozone/${geoZoneId}?`;
    const params: string[] = [];

    if (status) {
      params.push(`status=${encodeURIComponent(status)}`);
    }
    params.push(`pageIndex=${pageIndex}`);
    params.push(`pageSize=${pageSize}`);

    url += params.join('&');

    return this.http.get<UnsupportedLocationDtoPaginatedResponse>(url);
  }

  updateUnsupportedLocationStatus(
    locationId: string,
    request: UpdateUnsupportedLocationStatusRequestDto,
  ): Observable<UnsupportedLocationDto> {
    return this.http.put<UnsupportedLocationDto>(
      `${environment.apiConfig.uri}/api/GeoZone/unsupported-locations/${locationId}/status`,
      request,
    );
  }

  getScrapCollectionInsights(
    agentIds?: string,
    geoZoneIds?: string,
    fromDate?: string,
    toDate?: string,
  ): Observable<ScrapCollectionInsights> {
    let url = `${environment.apiConfig.uri}/api/ScrapCollection/insights?`;
    const params: string[] = [];

    if (agentIds) {
      params.push(`agentIds=${encodeURIComponent(agentIds)}`);
    }
    if (geoZoneIds) {
      params.push(`geoZoneIds=${encodeURIComponent(geoZoneIds)}`);
    }
    if (fromDate) {
      params.push(`fromDate=${encodeURIComponent(fromDate)}`);
    }
    if (toDate) {
      params.push(`toDate=${encodeURIComponent(toDate)}`);
    }

    url += params.join('&');

    if (!this.authService.isMsalInitialized()) {
      return new Observable((observer) => {
        this.authService
          .getMsalInitializedObservable()
          .pipe(
            filter((initialized) => initialized),
            take(1),
          )
          .subscribe(() => {
            this.http.get<ScrapCollectionInsights>(url).subscribe(observer);
          });
      });
    }

    return this.http.get<ScrapCollectionInsights>(url);
  }

  getScrapCollections(
    pageIndex: number = 0,
    pageSize: number = 10,
    dateFrom?: string,
    dateTo?: string,
    geoZoneId?: string,
    customerName?: string,
    phoneNumber?: string,
    agentId?: string,
    paymentMethod?: string,
    voucherSettlementStatus?: string,
    requiresFeedback?: boolean,
    feedbackStatus?: string,
  ): Observable<{
    items: ScrapCollection[];
    totalCount: number;
    pageIndex: number;
    pageSize: number;
  }> {
    let url = `${environment.apiConfig.uri}/api/ScrapCollection/all?pageIndex=${pageIndex}&pageSize=${pageSize}`;

    if (dateFrom) {
      url += `&dateFrom=${encodeURIComponent(dateFrom)}`;
    }
    if (dateTo) {
      url += `&dateTo=${encodeURIComponent(dateTo)}`;
    }
    if (geoZoneId) {
      url += `&geoZoneId=${encodeURIComponent(geoZoneId)}`;
    }
    if (customerName) {
      url += `&customerName=${encodeURIComponent(customerName)}`;
    }
    if (phoneNumber) {
      url += `&phoneNumber=${encodeURIComponent(phoneNumber)}`;
    }
    if (agentId) {
      url += `&agentId=${encodeURIComponent(agentId)}`;
    }
    if (paymentMethod) {
      url += `&paymentMethod=${encodeURIComponent(paymentMethod)}`;
    }
    if (voucherSettlementStatus) {
      url += `&voucherSettlementStatus=${encodeURIComponent(voucherSettlementStatus)}`;
    }
    if (requiresFeedback !== undefined && requiresFeedback !== null) {
      url += `&requiresFeedback=${requiresFeedback}`;
    }
    if (feedbackStatus) {
      url += `&feedbackStatus=${encodeURIComponent(feedbackStatus)}`;
    }

    if (!this.authService.isMsalInitialized()) {
      return new Observable((observer) => {
        this.authService
          .getMsalInitializedObservable()
          .pipe(
            filter((initialized) => initialized),
            take(1),
          )
          .subscribe(() => {
            this.http
              .get<{
                items: ScrapCollection[];
                totalCount: number;
                pageIndex: number;
                pageSize: number;
              }>(url)
              .subscribe(observer);
          });
      });
    }

    return this.http.get<{
      items: ScrapCollection[];
      totalCount: number;
      pageIndex: number;
      pageSize: number;
    }>(url);
  }

  updateVoucherSettlementBatch(
    request: BulkUpdateVoucherSettlementDto,
  ): Observable<any> {
    const url = `${environment.apiConfig.uri}/api/ScrapCollection/voucher-settlement/batch-update`;

    if (!this.authService.isMsalInitialized()) {
      return new Observable((observer) => {
        this.authService
          .getMsalInitializedObservable()
          .pipe(
            filter((initialized) => initialized),
            take(1),
          )
          .subscribe(() => {
            this.http.post<any>(url, request).subscribe(observer);
          });
      });
    }

    return this.http.post<any>(url, request);
  }

  getScrapCollectionById(
    scrapCollectionId: string,
  ): Observable<ScrapCollection> {
    const url = `${environment.apiConfig.uri}/api/ScrapCollection/${scrapCollectionId}`;

    if (!this.authService.isMsalInitialized()) {
      return new Observable((observer) => {
        this.authService
          .getMsalInitializedObservable()
          .pipe(
            filter((initialized) => initialized),
            take(1),
          )
          .subscribe(() => {
            this.http.get<ScrapCollection>(url).subscribe(observer);
          });
      });
    }

    return this.http.get<ScrapCollection>(url);
  }

  setScrapCollectionInvalid(scrapCollectionId: string): Observable<any> {
    const url = `${environment.apiConfig.uri}/api/ScrapCollection/Invalid/${scrapCollectionId}`;

    if (!this.authService.isMsalInitialized()) {
      return new Observable((observer) => {
        this.authService
          .getMsalInitializedObservable()
          .pipe(
            filter((initialized) => initialized),
            take(1),
          )
          .subscribe(() => {
            this.http.post<any>(url, {}).subscribe(observer);
          });
      });
    }

    return this.http.post<any>(url, {});
  }

  convertScrapCollectionToCash(
    scrapCollectionId: string,
  ): Observable<ScrapCollection> {
    const url = `${environment.apiConfig.uri}/api/ScrapCollection/${scrapCollectionId}/convert-to-cash`;

    if (!this.authService.isMsalInitialized()) {
      return new Observable((observer) => {
        this.authService
          .getMsalInitializedObservable()
          .pipe(
            filter((initialized) => initialized),
            take(1),
          )
          .subscribe(() => {
            this.http.post<ScrapCollection>(url, {}).subscribe(observer);
          });
      });
    }

    return this.http.post<ScrapCollection>(url, {});
  }

  logScrapCollectionFeedback(
    scrapCollectionId: string,
    payload: SaveScrapCollectionFeedbackDto,
  ): Observable<any> {
    const url = `${environment.apiConfig.uri}/api/ScrapCollection/${scrapCollectionId}/log-feedback`;
    return this.http.post<any>(url, payload);
  }

  tagScrapCollectionFeedback(
    scrapCollectionId: string,
    payload: TagScrapCollectionFeedbackDto,
  ): Observable<any> {
    const url = `${environment.apiConfig.uri}/api/ScrapCollection/${scrapCollectionId}/tag-feedback`;
    return this.http.post<any>(url, payload);
  }

  updateBulkRemittance(request: BulkRemittanceUpdateRequest): Observable<any> {
    if (!this.authService.isMsalInitialized()) {
      return new Observable((observer) => {
        this.authService
          .getMsalInitializedObservable()
          .pipe(
            filter((initialized) => initialized),
            take(1),
          )
          .subscribe(() => {
            this.http
              .put<any>(
                `${environment.apiConfig.uri}/api/PlatformChargeTransaction/bulk-remittance`,
                request,
              )
              .subscribe(observer);
          });
      });
    }
    return this.http.put<any>(
      `${environment.apiConfig.uri}/api/PlatformChargeTransaction/bulk-remittance`,
      request,
    );
  }

  // Marketing API Methods
  getMarketingPlacesByGeoZone(
    geoZoneId: string,
    placeType?: string,
    status?: string,
    pageIndex: number = 0,
    pageSize: number = 10,
  ): Observable<MarketingPlaceDtoPaginatedResponse> {
    let url = `${environment.apiConfig.uri}/api/Marketing/places/by-geozone/${geoZoneId}?`;
    const params: string[] = [];

    if (placeType) {
      params.push(`placeType=${encodeURIComponent(placeType)}`);
    }
    if (status) {
      params.push(`status=${encodeURIComponent(status)}`);
    }
    params.push(`pageIndex=${pageIndex}`);
    params.push(`pageSize=${pageSize}`);

    url += params.join('&');

    return this.http.get<MarketingPlaceDtoPaginatedResponse>(url);
  }

  getMarketingPlaceById(placeId: string): Observable<MarketingPlaceDto> {
    return this.http.get<MarketingPlaceDto>(
      `${environment.apiConfig.uri}/api/Marketing/places/${placeId}`,
    );
  }

  updateMarketingLeadStatus(
    placeId: string,
    request: UpdateMarketingLeadStatusRequestDto,
  ): Observable<MarketingPlaceDto> {
    return this.http.put<MarketingPlaceDto>(
      `${environment.apiConfig.uri}/api/Marketing/places/${placeId}/status`,
      request,
    );
  }

  getPlaceTypes(category?: string): Observable<GooglePlaceTypeDto[]> {
    let url = `${environment.apiConfig.uri}/api/Marketing/place-types`;
    if (category) {
      url += `?category=${encodeURIComponent(category)}`;
    }
    return this.http.get<GooglePlaceTypeDto[]>(url);
  }

  collectMarketingPlaces(
    request: CollectMarketingPlacesRequestDto,
  ): Observable<MarketingPlaceJobStatusDto> {
    return this.http.post<MarketingPlaceJobStatusDto>(
      `${environment.apiConfig.uri}/api/Marketing/places/collect`,
      request,
    );
  }

  getMarketingPlaceJobStatus(
    jobId: string,
  ): Observable<MarketingPlaceJobStatusDto> {
    return this.http.get<MarketingPlaceJobStatusDto>(
      `${environment.apiConfig.uri}/api/Marketing/places/jobs/${encodeURIComponent(jobId)}`,
    );
  }

  // Non Scrap Item API Methods
  getNonScrapItemTransactions(
    pageIndex: number = 0,
    pageSize: number = 10,
    agentId?: string,
    geoZoneId?: string,
    isSettled?: boolean,
    fromDate?: string,
    toDate?: string,
  ): Observable<{
    items: NonScrapItemTransactionDto[];
    totalCount: number;
    pageIndex: number;
    pageSize: number;
  }> {
    let url = `${environment.apiConfig.uri}/api/NonScrapItemTransaction?pageIndex=${pageIndex}&pageSize=${pageSize}`;

    if (agentId) {
      url += `&agentId=${encodeURIComponent(agentId)}`;
    }
    if (geoZoneId) {
      url += `&geoZoneId=${encodeURIComponent(geoZoneId)}`;
    }
    if (isSettled !== undefined && isSettled !== null) {
      url += `&isSettled=${isSettled}`;
    }
    if (fromDate) {
      url += `&fromDate=${encodeURIComponent(fromDate)}`;
    }
    if (toDate) {
      url += `&toDate=${encodeURIComponent(toDate)}`;
    }

    if (!this.authService.isMsalInitialized()) {
      return new Observable((observer) => {
        this.authService
          .getMsalInitializedObservable()
          .pipe(
            filter((initialized) => initialized),
            take(1),
          )
          .subscribe(() => {
            this.http
              .get<{
                items: NonScrapItemTransactionDto[];
                totalCount: number;
                pageIndex: number;
                pageSize: number;
              }>(url)
              .subscribe(observer);
          });
      });
    }

    return this.http.get<{
      items: NonScrapItemTransactionDto[];
      totalCount: number;
      pageIndex: number;
      pageSize: number;
    }>(url);
  }

  updateBulkSettlement(request: BulkSettlementUpdateRequest): Observable<any> {
    const url = `${environment.apiConfig.uri}/api/NonScrapItemtransaction/bulk-settlement`;

    if (!this.authService.isMsalInitialized()) {
      return new Observable((observer) => {
        this.authService
          .getMsalInitializedObservable()
          .pipe(
            filter((initialized) => initialized),
            take(1),
          )
          .subscribe(() => {
            this.http.put<any>(url, request).subscribe(observer);
          });
      });
    }

    return this.http.put<any>(url, request);
  }

  getFilteredVouchers(
    voucherFilter: VoucherFilterDto,
  ): Observable<{ items: VoucherDto[]; totalCount: number }> {
    // Build query parameters
    let url = `${environment.apiConfig.uri}/api/Voucher?pageIndex=${voucherFilter.pageIndex}&pageSize=${voucherFilter.pageSize}`;

    if (voucherFilter.phoneNumber) {
      url += `&phoneNumber=${encodeURIComponent(voucherFilter.phoneNumber)}`;
    }
    if (voucherFilter.customerName) {
      url += `&customerName=${encodeURIComponent(voucherFilter.customerName)}`;
    }
    if (voucherFilter.partnerName) {
      url += `&partnerName=${encodeURIComponent(voucherFilter.partnerName)}`;
    }
    if (
      voucherFilter.minVoucherAmount !== null &&
      voucherFilter.minVoucherAmount !== undefined
    ) {
      url += `&minVoucherAmount=${voucherFilter.minVoucherAmount}`;
    }
    if (
      voucherFilter.maxVoucherAmount !== null &&
      voucherFilter.maxVoucherAmount !== undefined
    ) {
      url += `&maxVoucherAmount=${voucherFilter.maxVoucherAmount}`;
    }
    if (voucherFilter.partnerId) {
      url += `&partnerId=${encodeURIComponent(voucherFilter.partnerId)}`;
    }
    if (voucherFilter.outletId) {
      url += `&outletId=${encodeURIComponent(voucherFilter.outletId)}`;
    }
    if (voucherFilter.status !== null && voucherFilter.status !== undefined) {
      url += `&status=${voucherFilter.status}`;
    }
    if (voucherFilter.redemptionReference) {
      url += `&redemptionReference=${encodeURIComponent(voucherFilter.redemptionReference)}`;
    }
    if (voucherFilter.fromDate) {
      url += `&fromDate=${encodeURIComponent(voucherFilter.fromDate)}`;
    }
    if (voucherFilter.toDate) {
      url += `&toDate=${encodeURIComponent(voucherFilter.toDate)}`;
    }

    // Ensure MSAL is initialized before making the request
    if (!this.authService.isMsalInitialized()) {
      return new Observable((observer) => {
        this.authService
          .getMsalInitializedObservable()
          .pipe(
            filter((initialized: boolean) => initialized),
            take(1),
          )
          .subscribe(() => {
            this.http
              .get<{ items: VoucherDto[]; totalCount: number } | VoucherDto[]>(
                url,
              )
              .pipe(
                map((response) => {
                  // Handle both paginated response and direct array response
                  if (Array.isArray(response)) {
                    return { items: response, totalCount: response.length };
                  }
                  return response as {
                    items: VoucherDto[];
                    totalCount: number;
                  };
                }),
              )
              .subscribe(observer);
          });
      });
    }
    return this.http
      .get<{ items: VoucherDto[]; totalCount: number } | VoucherDto[]>(url)
      .pipe(
        map((response) => {
          // Handle both paginated response and direct array response
          if (Array.isArray(response)) {
            return { items: response, totalCount: response.length };
          }
          return response as { items: VoucherDto[]; totalCount: number };
        }),
      );
  }

  // Partner API Methods
  getAllPartners(): Observable<PartnerDto[]> {
    if (!this.authService.isMsalInitialized()) {
      return new Observable((observer) => {
        this.authService
          .getMsalInitializedObservable()
          .pipe(
            filter((initialized: boolean) => initialized),
            take(1),
          )
          .subscribe(() => {
            this.http
              .get<PartnerDto[]>(`${environment.apiConfig.uri}/api/Partner`)
              .subscribe(observer);
          });
      });
    }
    return this.http.get<PartnerDto[]>(
      `${environment.apiConfig.uri}/api/Partner`,
    );
  }

  createPartner(partnerData: CreatePartnerDto): Observable<PartnerDto> {
    if (!this.authService.isMsalInitialized()) {
      return new Observable((observer) => {
        this.authService
          .getMsalInitializedObservable()
          .pipe(
            filter((initialized: boolean) => initialized),
            take(1),
          )
          .subscribe(() => {
            this.http
              .post<PartnerDto>(
                `${environment.apiConfig.uri}/api/Partner/create`,
                partnerData,
              )
              .subscribe(observer);
          });
      });
    }
    return this.http.post<PartnerDto>(
      `${environment.apiConfig.uri}/api/Partner/create`,
      partnerData,
    );
  }

  updatePartner(partnerData: PartnerDto): Observable<PartnerDto> {
    if (!this.authService.isMsalInitialized()) {
      return new Observable((observer) => {
        this.authService
          .getMsalInitializedObservable()
          .pipe(
            filter((initialized: boolean) => initialized),
            take(1),
          )
          .subscribe(() => {
            this.http
              .put<PartnerDto>(
                `${environment.apiConfig.uri}/api/Partner/update`,
                partnerData,
              )
              .subscribe(observer);
          });
      });
    }
    return this.http.put<PartnerDto>(
      `${environment.apiConfig.uri}/api/Partner/update`,
      partnerData,
    );
  }

  generatePartnerMonthlyInvoices(
    year: number,
    month: number,
  ): Observable<GenerateInvoicesResultDto> {
    const params = new HttpParams()
      .set('year', String(year))
      .set('month', String(month));
    const url = `${environment.apiConfig.uri}/api/Invoice/partners/generate-monthly`;

    if (!this.authService.isMsalInitialized()) {
      return new Observable((observer) => {
        this.authService
          .getMsalInitializedObservable()
          .pipe(
            filter((initialized: boolean) => initialized),
            take(1),
          )
          .subscribe(() => {
            this.http
              .post<GenerateInvoicesResultDto>(url, null, { params })
              .subscribe(observer);
          });
      });
    }
    return this.http.post<GenerateInvoicesResultDto>(url, null, { params });
  }

  getPartnerInvoices(partnerId: string): Observable<PartnerInvoiceDto[]> {
    const url = `${environment.apiConfig.uri}/api/Partner/invoices/${encodeURIComponent(partnerId)}`;

    const mapResponse = (response: any): PartnerInvoiceDto[] => {
      if (Array.isArray(response)) {
        return response;
      }
      if (response && Array.isArray(response.items)) {
        return response.items;
      }
      return [];
    };

    if (!this.authService.isMsalInitialized()) {
      return new Observable((observer) => {
        this.authService
          .getMsalInitializedObservable()
          .pipe(
            filter((initialized: boolean) => initialized),
            take(1),
          )
          .subscribe(() => {
            this.http
              .get<any>(url)
              .pipe(map(mapResponse))
              .subscribe(observer);
          });
      });
    }
    return this.http.get<any>(url).pipe(map(mapResponse));
  }

  // Voucher Configuration API Methods
  getAllVoucherConfigurations(
    partnerId?: string | null,
  ): Observable<VoucherConfigurationDto[]> {
    let url = `${environment.apiConfig.uri}/api/VoucherConfiguration`;
    if (partnerId) {
      url += `?partnerId=${encodeURIComponent(partnerId)}`;
    }

    if (!this.authService.isMsalInitialized()) {
      return new Observable((observer) => {
        this.authService
          .getMsalInitializedObservable()
          .pipe(
            filter((initialized: boolean) => initialized),
            take(1),
          )
          .subscribe(() => {
            this.http.get<VoucherConfigurationDto[]>(url).subscribe(observer);
          });
      });
    }
    return this.http.get<VoucherConfigurationDto[]>(url);
  }

  createVoucherConfiguration(
    configData: CreateVoucherConfigurationDto,
  ): Observable<VoucherConfigurationDto> {
    if (!this.authService.isMsalInitialized()) {
      return new Observable((observer) => {
        this.authService
          .getMsalInitializedObservable()
          .pipe(
            filter((initialized: boolean) => initialized),
            take(1),
          )
          .subscribe(() => {
            this.http
              .post<VoucherConfigurationDto>(
                `${environment.apiConfig.uri}/api/VoucherConfiguration/create`,
                configData,
              )
              .subscribe(observer);
          });
      });
    }
    return this.http.post<VoucherConfigurationDto>(
      `${environment.apiConfig.uri}/api/VoucherConfiguration/create`,
      configData,
    );
  }

  updateVoucherConfiguration(
    configData: VoucherConfigurationDto,
  ): Observable<VoucherConfigurationDto> {
    if (!this.authService.isMsalInitialized()) {
      return new Observable((observer) => {
        this.authService
          .getMsalInitializedObservable()
          .pipe(
            filter((initialized: boolean) => initialized),
            take(1),
          )
          .subscribe(() => {
            this.http
              .put<VoucherConfigurationDto>(
                `${environment.apiConfig.uri}/api/VoucherConfiguration/update`,
                configData,
              )
              .subscribe(observer);
          });
      });
    }
    return this.http.put<VoucherConfigurationDto>(
      `${environment.apiConfig.uri}/api/VoucherConfiguration/update`,
      configData,
    );
  }

  deleteVoucherConfiguration(id: string, partnerId: string): Observable<any> {
    const url = `${environment.apiConfig.uri}/api/VoucherConfiguration/${id}?partnerId=${encodeURIComponent(partnerId)}`;
    if (!this.authService.isMsalInitialized()) {
      return new Observable((observer) => {
        this.authService
          .getMsalInitializedObservable()
          .pipe(
            filter((initialized: boolean) => initialized),
            take(1),
          )
          .subscribe(() => {
            this.http.delete(url).subscribe(observer);
          });
      });
    }
    return this.http.delete(url);
  }

  getGlobalSettings(): Observable<GlobalSettingsDto> {
    return this.http.get<GlobalSettingsDto>(
      `${environment.apiConfig.uri}/api/GlobalSettings`,
    );
  }

  updateGlobalSettings(settings: UpdateGlobalSettingsDto): Observable<{ id: string }> {
    return this.http.post<{ id: string }>(
      `${environment.apiConfig.uri}/api/GlobalSettings/create`,
      settings
    );
  }

  createHoliday(holiday: CreateHolidayDto): Observable<{ id: string }> {
    return this.http.post<{ id: string }>(
      `${environment.apiConfig.uri}/api/Holiday/create`,
      holiday
    );
  }

  getHolidays(
    filter: HolidayFilter,
    geoZoneId?: string,
    type?: string,
  ): Observable<Holiday[]> {
    let url = `${environment.apiConfig.uri}/api/Holiday/list/filter`;
    const params: string[] = [];
    if (geoZoneId) {
      params.push(`geoZoneId=${encodeURIComponent(geoZoneId)}`);
    }
    if (type) {
      params.push(`type=${encodeURIComponent(type)}`);
    }
    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }
    return this.http.post<Holiday[]>(url, filter);
  }

  deleteHoliday(holiday: Pick<Holiday, 'id' | 'type'>): Observable<void> {
    return this.http.delete<void>(
      `${environment.apiConfig.uri}/api/Holiday/${encodeURIComponent(holiday.type)}/${encodeURIComponent(holiday.id)}`,
    );
  }

  // WhatsApp Customer Care Queue API Methods
  getWhatsAppCareQueue(filter: WhatsAppCareQueueFilterDto): Observable<WhatsAppCareQueuePaginatedResponse> {
    return this.http.post<WhatsAppCareQueuePaginatedResponse>(`${environment.apiConfig.uri}/api/WhatsAppCareQueue/filter`, filter);
  }

  markWhatsAppCareQueueItemAttended(sessionId: string): Observable<WhatsAppCareQueueItem> {
    return this.http.put<WhatsAppCareQueueItem>(`${environment.apiConfig.uri}/api/WhatsAppCareQueue/${sessionId}/attend`, {});
  }

  launchGeoZoneNotification(request: GeoZoneLaunchNotificationRequestDto): Observable<GeoZoneLaunchNotificationJobAcceptedDto> {
    const url = `${environment.apiConfig.uri}/api/Sms/geo-zone-launch-notifications`;
    return this.http.post<GeoZoneLaunchNotificationJobAcceptedDto>(url, request);
  }

  getGeoZoneLaunchSendLog(
    geoZoneId: string,
    status?: string,
    pageIndex: number = 0,
    pageSize: number = 20,
  ): Observable<WhatsAppCampaignSendLogPaginatedResponse> {
    let url = `${environment.apiConfig.uri}/api/Sms/geo-zone-launch-notifications/${geoZoneId}/send-log?`;
    const params: string[] = [];

    if (status) {
      params.push(`status=${encodeURIComponent(status)}`);
    }
    params.push(`pageIndex=${pageIndex}`);
    params.push(`pageSize=${pageSize}`);

    url += params.join('&');

    return this.http.get<WhatsAppCampaignSendLogPaginatedResponse>(url);
  }

  getWhatsAppCampaignSendLog(
    campaignId: string,
    status?: string,
    pageIndex: number = 0,
    pageSize: number = 20,
  ): Observable<WhatsAppCampaignSendLogPaginatedResponse> {
    let url = `${environment.apiConfig.uri}/api/Sms/whatsapp-campaigns/${campaignId}/send-log?`;
    const params: string[] = [];

    if (status) {
      params.push(`status=${encodeURIComponent(status)}`);
    }
    params.push(`pageIndex=${pageIndex}`);
    params.push(`pageSize=${pageSize}`);

    url += params.join('&');

    return this.http.get<WhatsAppCampaignSendLogPaginatedResponse>(url);
  }

  // Send log across every campaign, newest first. campaignIdPrefix narrows to campaigns whose id
  // starts with it (e.g. "GeoZoneLaunch:" for every zone); omit for all campaigns.
  getAllWhatsAppCampaignSendLog(
    campaignIdPrefix?: string,
    status?: string,
    pageIndex: number = 0,
    pageSize: number = 20,
  ): Observable<WhatsAppCampaignSendLogPaginatedResponse> {
    let url = `${environment.apiConfig.uri}/api/Sms/whatsapp-campaigns/send-log?`;
    const params: string[] = [];

    if (campaignIdPrefix) {
      params.push(`campaignIdPrefix=${encodeURIComponent(campaignIdPrefix)}`);
    }
    if (status) {
      params.push(`status=${encodeURIComponent(status)}`);
    }
    params.push(`pageIndex=${pageIndex}`);
    params.push(`pageSize=${pageSize}`);

    url += params.join('&');

    return this.http.get<WhatsAppCampaignSendLogPaginatedResponse>(url);
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}
